/**
 * Wires `contacts.model.ts` to the contacts screen's reads and writes.
 *
 * Calls `fetchContacts` once for the list and the reader stamp, and
 * `fetchContactTimeline` only when a row is opened; writes are
 * `setContactNote`, `endContactNote`, `addContact` and `endAddedContact`, and
 * every one ends in a refetch rather than a patch.
 *
 * THE EXPORT IS A REQUEST NOW — OND-67. It used to build a `Blob` from the
 * rendered view and make no request at all, with the argument that a file
 * fetched again could disagree with the screen it came from. That argument was
 * answered rather than overturned: the scope the screen is showing is SENT, so
 * the server narrows the same way, and what the request buys is the thing a
 * local `Blob` could never have — an audit row naming the reader, the firm and
 * every record, written before the bytes exist and refusing the export if it
 * cannot be written.
 *
 * THE TIMELINE'S THUMBNAILS DO NOT GO THROUGH `signWorkThumbnails`, and that is
 * the one place this screen differs from every other list in the app. A signed
 * storage URL renders the ORIGINAL; inside this screen a photograph has to
 * carry the name of whoever was shown it, so the bytes come from the API
 * already stamped and become object URLs this controller owns and revokes.
 *
 * The state the model cannot hold is the filter and search text, the
 * add-contact modal's open flag, draft, busy and problem, which contact is
 * open, which note is mid-flight, the export's in-flight flag, the stamped
 * thumbnails, and the reload counter. The fetched timeline is keyed by the
 * contact it belongs to, so "loading" is a mismatch between the opener and what
 * is held rather than a flag to clear.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Contact, ContactEvent, ReaderStamp } from "@ondo/shared";

import {
  addContact,
  endAddedContact,
  endContactNote,
  exportCollectorList,
  fetchContactTimeline,
  fetchContacts,
  fetchStampedWorkImage,
  setContactNote,
} from "../lib/api.ts";
import { ApiError } from "../lib/api.ts";
import {
  ALL,
  EMPTY_ADD_DRAFT,
  buildAddContactRequest,
  describeContactsError,
  toAddContactView,
  toContactsView,
  toExportScope,
  toStampView,
  toTimelineRows,
} from "./contacts.model.ts";
import type {
  AddContactDraft,
  AddContactView,
  ContactFilter,
  ContactsView,
  StampView,
  TimelineView,
} from "./contacts.model.ts";
import { CONTACTS_COPY } from "./contacts.constants.ts";

export interface ContactsController {
  view: ContactsView;
  /**
   * Who the SERVER says is reading this list. Null only while the list is
   * loading or after it failed — there is no state in which rows are on screen
   * and this is absent.
   */
  stamp: StampView | null;
  /** The open contact's shared history, or `closed`. */
  timeline: TimelineView;
  /** Filter by relationship. A filter over loaded rows, not a query. */
  choose: (filter: ContactFilter) => void;
  /** The search box. A filter over loaded rows — never a query. */
  search: string;
  setSearch: (value: string) => void;
  /** The add-contact modal, already decided. */
  add: AddContactView;
  openAdd: () => void;
  /** Shuts the dialog. The draft survives — Cancel is what discards. */
  closeAdd: () => void;
  cancelAdd: () => void;
  setAddDraft: (patch: Partial<AddContactDraft>) => void;
  submitAdd: () => void;
  /** Retract an added contact. The row survives; the list refetches. */
  removeAdded: (addedId: string) => void;
  /** Open one contact's history. Attached contacts only — a name has no id to ask by. */
  open: (ondoId: string, name: string) => void;
  close: () => void;
  /** Write or replace the firm's note on one contact. */
  saveNote: (subject: NoteSubject, body: string, tags: string[]) => void;
  /** Stop keeping one. The row survives. */
  clearNote: (noteId: string) => void;
  /** Which contact's note is mid-flight, so its control can be disabled. */
  saving: string | null;
  /** Ask the server for what is on screen, as a file it records. */
  exportCsv: () => void;
  /** True while the server is building and recording the file. */
  exporting: boolean;
  /** Why the server refused to release a file. Its own sentence where it gave one. */
  exportProblem: string | null;
}

/** An id, or the exact name — the two ways a contact can be addressed. */
export type NoteSubject = { ondoId: string } | { subjectName: string };

/** Stable across both kinds, for `saving`. */
function subjectKey(subject: NoteSubject): string {
  return "ondoId" in subject ? `id:${subject.ondoId}` : `name:${subject.subjectName}`;
}

/**
 * The gallery's contacts, from one read, plus one more when a row is opened.
 *
 * ONE REQUEST FOR THE LIST, deliberately, where the browser could have assembled
 * it from the four gallery screens that already name counterparties. Four
 * requests would mean four notions of who a contact is and a fifth opinion here
 * about how to merge them — and the merge is the whole feature, so it belongs
 * where the rows are.
 *
 * THE TIMELINE IS FETCHED ON OPEN, not with the list. A gallery with two hundred
 * contacts would otherwise pay for two hundred histories to render one. It is
 * also the only place this screen sends an id anywhere, and it sends one the
 * list handed back a moment ago.
 *
 * THREE WRITES NOW — a note, an added contact, a retraction — and all three
 * end the same way: refetch, never patch. What the firm keeps is the
 * database's answer, and a local edit would be a second opinion about it.
 *
 * @param workBase '/gallery/ONDO-6624/work' — where a timeline row links to.
 * @param actingAs The workspace organisation's ONDO id, or null on a personal
 *   workspace. It is one of the READER'S OWN seats, taken from `/v1/me`, and
 *   the server refuses any id the caller does not hold one in — so it chooses
 *   between the reader's firms and cannot assert an identity.
 */
export function useContactsController(
  workBase: string,
  actingAs: string | null = null,
): ContactsController {
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [stamp, setStamp] = useState<ReaderStamp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ContactFilter>(ALL);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraftState] = useState<AddContactDraft>(EMPTY_ADD_DRAFT);
  const [addBusy, setAddBusy] = useState(false);
  const [addProblem, setAddProblem] = useState<string | null>(null);
  const [openId, setOpenId] = useState<{ ondoId: string; name: string } | null>(null);
  /**
   * KEYED BY THE CONTACT IT BELONGS TO, so that "loading" is a MISMATCH rather
   * than a flag somebody has to remember to clear. Holding a bare array would
   * mean resetting it as the effect opens — a synchronous setState in an effect
   * body, which cascades a render and which `useMe` avoids the same way.
   */
  const [loaded, setLoaded] = useState<{ ondoId: string; events: ContactEvent[] } | null>(
    null,
  );
  const [failed, setFailed] = useState<{ ondoId: string; message: string } | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportProblem, setExportProblem] = useState<string | null>(null);
  /**
   * The stamped photographs for the open timeline, keyed by work id.
   *
   * OBJECT URLS THIS CONTROLLER OWNS, over bytes the server composited. They
   * are revoked when the drawer's contents change or the screen unmounts: a
   * blob URL that is never revoked keeps a stamped image of somebody's work
   * alive in the tab for as long as it is open.
   */
  const [thumbs, setThumbs] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let active = true;

    fetchContacts(actingAs)
      .then((found) => {
        if (!active) return;
        setContacts(found.contacts);
        setStamp(found.stamp);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setContacts([]);
        // THE STAMP IS CLEARED WITH THE ROWS. If the read failed there is no
        // server-derived identity, and a stamp left over from a previous read
        // would be a name on a screen that is no longer showing that read.
        setStamp(null);
        setError(describeContactsError(cause));
      });

    return () => {
      active = false;
    };
    // Refetched after a note is written, never patched in place: what the firm
    // remembers is the database's answer, and a local edit would be a second
    // opinion about it.
  }, [reloads, actingAs]);

  useEffect(() => {
    if (!openId) return;
    const asked = openId.ondoId;
    let active = true;

    fetchContactTimeline(asked)
      .then((found) => {
        if (active) setLoaded({ ondoId: asked, events: found });
      })
      .catch(() => {
        // Deliberately not `describeContactsError`: a failure to load one
        // history is not the same event as a failure to load the list, and a
        // 403 here would be about the drawer rather than the seat.
        if (active) setFailed({ ondoId: asked, message: CONTACTS_COPY.timelineFailed });
      });

    return () => {
      active = false;
    };
  }, [openId]);

  /**
   * The stamped photographs for whatever the drawer is showing.
   *
   * A SECOND PASS, after the rows land, exactly as `useWorkThumbnails` does for
   * every other list — a slow image must never hold up a history that has
   * already arrived. What differs is the source: these come from the API with
   * the reader's name already in the pixels, so there is no signed storage URL
   * anywhere in this screen and nothing here can render an unstamped original.
   *
   * Each fetch settles on its own. One work whose photograph storage refuses is
   * simply absent from the map, and its row shows a placeholder rather than
   * failing the drawer around it.
   */
  useEffect(() => {
    if (!loaded) return;

    let active = true;
    const minted: string[] = [];
    const works = [...new Set(loaded.events.flatMap((event) => event.workId ?? []))];

    void Promise.all(
      works.map(async (workId) => {
        try {
          const blob = await fetchStampedWorkImage(workId, actingAs);
          const url = URL.createObjectURL(blob);
          minted.push(url);
          return [workId, url] as const;
        } catch {
          // Being refused the bytes is a state and not a fault. The row keeps
          // its title and loses its picture.
          return null;
        }
      }),
    ).then((pairs) => {
      if (!active) return;
      setThumbs(new Map(pairs.filter((pair) => pair !== null)));
    });

    return () => {
      active = false;
      // Revoked on the way out, including the ones a stale fetch minted after
      // the drawer moved on. Nothing holds a stamped image longer than the
      // screen showing it.
      for (const url of minted) URL.revokeObjectURL(url);
      setThumbs(new Map());
    };
  }, [loaded, actingAs]);

  const open = useCallback((ondoId: string, name: string) => {
    setOpenId({ ondoId, name });
  }, []);

  // Only the opener is cleared. What was fetched can stay: it is keyed by the
  // contact it belongs to, so it can never be shown against another one.
  const close = useCallback(() => setOpenId(null), []);

  const saveNote = useCallback((subject: NoteSubject, body: string, tags: string[]) => {
    setSaving(subjectKey(subject));
    setContactNote({ ...subject, body, tags })
      .then(() => setReloads((n) => n + 1))
      .catch((cause: unknown) => setError(describeContactsError(cause)))
      .finally(() => setSaving(null));
  }, []);

  const clearNote = useCallback((noteId: string) => {
    setSaving(noteId);
    endContactNote(noteId)
      .then(() => setReloads((n) => n + 1))
      .catch((cause: unknown) => setError(describeContactsError(cause)))
      .finally(() => setSaving(null));
  }, []);

  const openAdd = useCallback(() => {
    setAddProblem(null);
    setAddOpen(true);
  }, []);

  // Escape and the backdrop land here. Only the OPEN flag moves — the draft
  // survives, because a stray Escape must not cost what was typed.
  const closeAdd = useCallback(() => setAddOpen(false), []);

  const cancelAdd = useCallback(() => {
    setAddOpen(false);
    setAddDraftState(EMPTY_ADD_DRAFT);
    setAddProblem(null);
  }, []);

  const setAddDraft = useCallback((patch: Partial<AddContactDraft>) => {
    // A typed change makes the last refusal stale — it was about the old body.
    setAddProblem(null);
    setAddDraftState((draft) => ({ ...draft, ...patch }));
  }, []);

  const submitAdd = useCallback(() => {
    setAddBusy(true);
    addContact(buildAddContactRequest(addDraft))
      .then(() => {
        setAddOpen(false);
        setAddDraftState(EMPTY_ADD_DRAFT);
        setAddProblem(null);
        setReloads((n) => n + 1);
      })
      .catch((cause: unknown) => {
        // The server's own sentence, verbatim — one refusal per branch, and a
        // second explanation here would be a second opinion about it. Covers
        // the 429 too: its message names the throttle, not the address.
        setAddProblem(
          cause instanceof ApiError ? cause.message : CONTACTS_COPY.failed,
        );
      })
      .finally(() => setAddBusy(false));
  }, [addDraft]);

  const removeAdded = useCallback((addedId: string) => {
    setSaving(addedId);
    endAddedContact(addedId)
      .then(() => setReloads((n) => n + 1))
      .catch((cause: unknown) => setError(describeContactsError(cause)))
      .finally(() => setSaving(null));
  }, []);

  // MEMOISED so `exportCsv` below has a stable dependency. Without it the
  // view object is new on every render, the callback is rebuilt with it, and
  // the memo is decoration.
  const view: ContactsView = useMemo(
    () =>
      error
        ? { kind: "error", message: error }
        : toContactsView({ contacts, filter, search }),
    [error, contacts, filter, search],
  );

  const add: AddContactView = useMemo(
    () =>
      toAddContactView({ open: addOpen, draft: addDraft, busy: addBusy, problem: addProblem }),
    [addOpen, addDraft, addBusy, addProblem],
  );

  const exportCsv = useCallback(() => {
    if (view.kind !== "ready") return;

    setExporting(true);
    setExportProblem(null);

    // THE SERVER BUILDS THE FILE. The scope the screen is showing is sent, so
    // what comes back is the page the reader is looking at — and the request
    // is what makes the export auditable at all: `ondo.export_collector_list()`
    // writes the record naming this reader before these bytes exist, and
    // refuses to hand them over if it cannot.
    exportCollectorList({ actingAs, ...toExportScope({ filter, search }) })
      .then((file) => {
        const url = URL.createObjectURL(file.blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        // The server's name, which carries the export id — that is how a file
        // in a downloads folder is matched to the row that authorised it.
        anchor.download = file.filename ?? "ondo-collector-list.csv";

        // IN THE DOCUMENT, AND REVOKED LATER. Both matter and neither is
        // superstition: a detached anchor is ignored by some browsers, and
        // revoking synchronously after `click()` can cancel the download
        // before it has read the blob — the click only SCHEDULES the save.
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      })
      .catch((cause: unknown) => {
        // The server's own sentence where it gave one. A refused export means
        // no file was released, which is the thing the reader needs told.
        setExportProblem(
          cause instanceof ApiError ? cause.message : CONTACTS_COPY.exportFailed,
        );
      })
      .finally(() => setExporting(false));
  }, [view, actingAs, filter, search]);

  return {
    view,
    stamp: stamp ? toStampView(stamp) : null,
    timeline: describeTimeline({ openId, loaded, failed, workBase, thumbs }),
    choose: setFilter,
    search,
    setSearch,
    add,
    openAdd,
    closeAdd,
    cancelAdd,
    setAddDraft,
    submitAdd,
    removeAdded,
    open,
    close,
    saveNote,
    clearNote,
    saving,
    exportCsv,
    exporting,
    exportProblem,
  };
}

/** The drawer's four states, kept a union so no two can be true at once. */
function describeTimeline({
  openId,
  loaded,
  failed,
  workBase,
  thumbs,
}: {
  openId: { ondoId: string; name: string } | null;
  loaded: { ondoId: string; events: ContactEvent[] } | null;
  failed: { ondoId: string; message: string } | null;
  workBase: string;
  /** Stamped photographs, keyed by work id. Empty until the second pass lands. */
  thumbs: ReadonlyMap<string, string>;
}): TimelineView {
  if (!openId) return { kind: "closed" };
  if (failed && failed.ondoId === openId.ondoId) {
    return { kind: "error", name: openId.name, message: failed.message };
  }
  // THE MISMATCH IS THE LOADING STATE. Anything held for a different contact is
  // stale by definition, so there is nothing to clear and no window in which
  // one contact's history can appear under another's name.
  if (!loaded || loaded.ondoId !== openId.ondoId) {
    return { kind: "loading", name: openId.name };
  }

  const rows = toTimelineRows(loaded.events, workBase, thumbs);
  return {
    kind: "ready",
    name: openId.name,
    rows,
    // An empty history is a real answer here rather than an error: it is what
    // an id that is not a contact of yours returns, and the two are
    // indistinguishable on purpose.
    empty: rows.length === 0 ? CONTACTS_COPY.timelineEmpty : null,
  };
}
